// Moved whole from `src/hooks/mutation/useMutateRequestTeamPlanUpgrade.ts`
// (which is now a name-intact delegate) at D2, ARCFE-972.
//
// This is the cohort's ONLY fully-headless move: its measured blockers were
// all mechanical — transport (healed by the D1/D2 api carve onto the E5
// registry), `@/utils/reactQueryClient` (S4 → the seam), and the `GET_USER`
// cache key, which is a FOREIGN CONTRACT and therefore a legal import: `/data`
// may reach another domain through its `/contracts` door, never its `/data`.
// No toast, no translations, no store read — so nothing has to stay app-side.
//
// AND NOTHING DOES, as of ARCFE-1111. The one leg left behind was a single
// `logger.error`, which D2 crossed as an `onRequestUpgradeError` port so the
// delegate would own the message string. Measured against delivery §7a: logging
// is NOT an app effect — `@heidi/runtime/logger` is importable here, and
// `@/utils/logger/logger` is a five-line runtime re-export of exactly that
// module — so the port was buying a permanent app-layer file that Z can never
// delete in exchange for nothing the ladder asks for. The log moves in with its
// message verbatim and the delegate dissolves to a pure re-export shim.

import { logger } from '@heidi/runtime/logger';
import { GET_USER, type UserResponse } from '@heidi/workspace/contracts';
import { useMutation } from '@tanstack/react-query';
import { produce } from 'immer';
import {
  type RequestSubscriptionUpgradeRequest,
  type RequestSubscriptionUpgradeResponse,
} from '../../contracts';
import { requestTeamPlanUpgrade } from '../api/subscription';
import { getCommerceDataConfig } from '../config';

export const REQUEST_TEAM_PLAN_UPGRADE_MUTATION_KEY = ['requestTeamPlanUpgrade'] as const;

export type UseMutateRequestTeamPlanUpgradeVariables = {
  teamId: string;
  body: RequestSubscriptionUpgradeRequest;
};

/**
 * POST /teams/{team_id}/request-plan-upgrade.
 *
 * The optimistic `setQueryData` on the user cache is preserved EXACTLY: the
 * same immer producer, the same append-if-absent guard, and the invalidate
 * still runs after it. That ordering is load-bearing — the write paints the
 * requesting user's button as already-contacted before the refetch lands.
 */
export function useMutateRequestTeamPlanUpgrade() {
  const { queryClient } = getCommerceDataConfig();

  return useMutation<
    RequestSubscriptionUpgradeResponse,
    unknown,
    UseMutateRequestTeamPlanUpgradeVariables
  >({
    mutationKey: [...REQUEST_TEAM_PLAN_UPGRADE_MUTATION_KEY],
    mutationFn: async ({ teamId, body }) => {
      const { data } = await requestTeamPlanUpgrade(teamId, body);
      return data;
    },
    onSuccess: async (_data, variables) => {
      queryClient.setQueryData<UserResponse>([GET_USER], (previous) => {
        if (!previous) return previous;
        return produce(previous, (draft) => {
          const existing = draft.contacted_admin_for_subscription_upgrade_team_ids ?? [];
          if (!existing.includes(variables.teamId)) {
            draft.contacted_admin_for_subscription_upgrade_team_ids = [
              ...existing,
              variables.teamId,
            ];
          }
        });
      });

      await queryClient.invalidateQueries({ queryKey: [GET_USER] });
    },
    onError: (error) => {
      logger.error('Failed to request team plan upgrade', error);
    },
  });
}
